import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, Stack, Paper, IconButton,
  Tooltip, LinearProgress, Divider, CircularProgress, Alert,
} from '@mui/material';
import {
  ChevronLeft, ChevronRight, Phone as PhoneIcon, Email as EmailIcon,
  OpenInNew as OpenIcon, Sync as SyncIcon, Description as AppIcon,
  AccountBalance as SbaIcon, Person as PersonIcon,
  PhoneAndroid as MobilePhoneIcon, Business as BusinessIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import salesAPI from '../../api/sales';
import { STATUS_CONFIG, PRIORITY_COLORS } from './SalesDashboard';
import StatusChanger from './StatusChanger';
import QuickNoteBar from './QuickNoteBar';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
const currency = (n) => n != null ? `$${Number(n).toLocaleString()}` : null;

// ─── Completion Ring ──────────────────────────────────────────────────────────
function CompletionRing({ value, label, color, icon }) {
  return (
    <Box sx={{ textAlign: 'center', position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        {/* Background ring */}
        <CircularProgress
          variant="determinate"
          value={100}
          size={72}
          thickness={5}
          sx={{ color: 'action.hover' }}
        />
        {/* Foreground ring */}
        <CircularProgress
          variant="determinate"
          value={value}
          size={72}
          thickness={5}
          sx={{ color, position: 'absolute', left: 0 }}
        />
        <Box sx={{
          position: 'absolute', top: 0, left: 0, bottom: 0, right: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }}>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1, fontSize: '1.1rem' }}>
            {value}%
          </Typography>
        </Box>
      </Box>
      <Stack direction="row" gap={0.5} alignItems="center" mt={0.5}>
        {icon}
        <Typography variant="caption" fontWeight={600} color="text.secondary">{label}</Typography>
      </Stack>
    </Box>
  );
}

// ─── User Card ────────────────────────────────────────────────────────────────
function UserCard({ lead, onOpenDetail, onStatusChange, onLeadUpdated }) {
  const status = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new_lead;
  const app = lead.applicationCompletion || {};
  const businessPhone = lead.businessPhone || lead.phone;
  const mobilePhone = lead.mobilePhone;

  return (
    <Paper
      elevation={3}
      sx={{
        flex: 1,
        maxWidth: 800,
        mx: 'auto',
        borderRadius: 3,
        overflow: 'hidden',
        border: '2px solid',
        borderColor: status.color,
      }}
    >
      {/* ── Top bar ──────────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: status.color, px: 2.5, py: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" gap={1} alignItems="center">
            <Chip
              label={status.label}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 700, fontSize: '0.75rem' }}
            />
            <Chip
              label={lead.priority?.toUpperCase()}
              size="small"
              sx={{ bgcolor: PRIORITY_COLORS[lead.priority] || '#94a3b8', color: '#fff', fontWeight: 700, fontSize: '0.68rem' }}
            />
            {lead.source && (
              <Chip
                label={lead.source.replace(/_/g, ' ').toUpperCase()}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600, fontSize: '0.65rem' }}
              />
            )}
          </Stack>
          <Tooltip title="Open full details">
            <IconButton size="small" onClick={() => onOpenDetail(lead)} sx={{ color: '#fff' }}>
              <OpenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Box sx={{ p: 2.5 }}>
        {/* ── User Identity ──────────────────────────────────────────────── */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box>
            <Typography variant="h5" fontWeight={800} color="text.primary" sx={{ lineHeight: 1.2 }}>
              {[lead.firstName, lead.lastName].filter(Boolean).join(' ') || 'Unknown'}
            </Typography>
            {lead.businessName && (
              <Typography variant="body1" fontWeight={600} color="text.secondary">
                {lead.businessName}
              </Typography>
            )}
            {lead.title && (
              <Typography variant="caption" color="text.disabled">{lead.title}</Typography>
            )}
          </Box>
          <Box textAlign="right">
            {lead.tags?.includes('auto-synced') && (
              <Chip label="Auto-Synced" size="small" color="info" variant="outlined" sx={{ fontSize: '0.65rem' }} />
            )}
          </Box>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* ── Completion Progress ────────────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{ p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
        >
          <Typography variant="subtitle2" fontWeight={700} mb={1.5} color="text.secondary">
            Application Completion
          </Typography>
          <Stack direction="row" justifyContent="space-around" alignItems="center">
            <CompletionRing
              value={app.generalPct || 0}
              label="Core Funding"
              color={app.generalPct >= 100 ? '#10b981' : app.generalPct > 0 ? '#f59e0b' : '#e5e7eb'}
              icon={<AppIcon sx={{ fontSize: 14, color: 'text.disabled' }} />}
            />
            <CompletionRing
              value={app.sbaPct || 0}
              label="SBA 413"
              color={app.sbaPct >= 100 ? '#10b981' : app.sbaPct > 0 ? '#f59e0b' : '#e5e7eb'}
              icon={<SbaIcon sx={{ fontSize: 14, color: 'text.disabled' }} />}
            />
            <CompletionRing
              value={app.overallPct || 0}
              label="Overall"
              color={app.overallPct >= 100 ? '#10b981' : app.overallPct > 50 ? '#3b82f6' : app.overallPct > 0 ? '#f59e0b' : '#e5e7eb'}
              icon={<PersonIcon sx={{ fontSize: 14, color: 'text.disabled' }} />}
            />
          </Stack>

          {!app.hasApplication && (
            <Alert severity="info" sx={{ mt: 1.5, py: 0.25 }}>
              No application submitted yet
            </Alert>
          )}
          {app.hasApplication && app.appStatus && (
            <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={1}>
              Application Status: <strong>{app.appStatus.replace(/_/g, ' ')}</strong>
            </Typography>
          )}
        </Paper>

        {/* ── Info Grid ──────────────────────────────────────────────────── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px', mb: 2 }}>
          <InfoRow label="Business Phone" value={businessPhone} href={businessPhone ? `tel:${businessPhone}` : null} />
          <InfoRow label="Mobile Phone" value={mobilePhone} href={mobilePhone ? `tel:${mobilePhone}` : null} />
          <InfoRow label="Email" value={lead.email} href={lead.email ? `mailto:${lead.email}` : null} />
          <InfoRow label="Monthly Revenue" value={currency(lead.monthlyRevenue)} />
          <InfoRow label="Requested Funding" value={currency(app.fundingAmount || lead.requestedAmount)} />
          <InfoRow label="Business Start" value={fmtDate(lead.businessStartDate)} />
          <InfoRow label="Entity Type" value={lead.businessEntityType} />
          <InfoRow label="EIN" value={lead.businessEIN} />
          <InfoRow label="Address" value={[lead.businessCity, lead.businessState].filter(Boolean).join(', ') || lead.businessAddress} />
        </Box>

        {/* ── CTA Buttons ────────────────────────────────────────────────── */}
        <Stack direction="row" gap={1.5} flexWrap="wrap">
          {businessPhone && (
            <Button
              variant="contained"
              color="success"
              startIcon={<BusinessIcon />}
              href={`tel:${businessPhone}`}
              component="a"
              size="medium"
              sx={{ fontWeight: 700, flex: 1, minWidth: 140 }}
            >
              Call Business
            </Button>
          )}
          {mobilePhone && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<MobilePhoneIcon />}
              href={`tel:${mobilePhone}`}
              component="a"
              size="medium"
              sx={{ fontWeight: 700, flex: 1, minWidth: 140 }}
            >
              Call Mobile
            </Button>
          )}
          {lead.email && (
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={() => onOpenDetail(lead)}
              size="medium"
              sx={{ flex: 1, minWidth: 120 }}
            >
              Email
            </Button>
          )}
        </Stack>

        {/* ── Last note ──────────────────────────────────────────────────── */}
        {lead.notes && lead.notes.length > 0 && (
          <Box mt={2} sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 1.5 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={0.5}>
              Last Note
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {lead.notes[0].content}
            </Typography>
          </Box>
        )}

        {/* ── Status changer + Quick note ────────────────────────────────── */}
        <Box mt={2}>
          <StatusChanger currentStatus={lead.status} onStatusChange={(s) => onStatusChange(lead._id, s)} />
        </Box>
        <Box mt={1.5}>
          <QuickNoteBar leadId={lead._id} onLeadUpdated={onLeadUpdated} />
        </Box>
      </Box>
    </Paper>
  );
}

// ─── InfoRow ──────────────────────────────────────────────────────────────────
function InfoRow({ label, value, href }) {
  if (!value) return null;
  return (
    <Box>
      <Typography variant="caption" color="text.disabled" display="block" lineHeight={1.2}>{label}</Typography>
      {href
        ? <Typography variant="body2" component="a" href={href} sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 500 }}>{value}</Typography>
        : <Typography variant="body2" fontWeight={500}>{value}</Typography>
      }
    </Box>
  );
}

// ─── Main UserCardsView ───────────────────────────────────────────────────────
export default function UserCardsView({ onOpenDetail, onStatusChange, onLeadUpdated, onSyncComplete }) {
  const [cards, setCards] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [total, setTotal] = useState(0);

  const fetchCards = useCallback(async () => {
    setLoading(true);
    try {
      const res = await salesAPI.getEnrichedLeads({ limit: 200, sortBy: 'createdAt', order: 'desc' });
      setCards(res.data.data);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Failed to load user cards');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCards(); }, [fetchCards]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
      const res = await fetch(`${API_URL}/api/admin/sync-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        await fetchCards();
        if (onSyncComplete) onSyncComplete();
      } else {
        toast.error(data.message || 'Sync failed');
      }
    } catch (err) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleCardUpdated = (updatedLead) => {
    setCards(prev => prev.map(c => c._id === updatedLead._id ? { ...c, ...updatedLead } : c));
    if (onLeadUpdated) onLeadUpdated(updatedLead);
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  }

  if (cards.length === 0) {
    return (
      <Paper elevation={0} sx={{ textAlign: 'center', py: 8, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
        <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography color="text.secondary" mb={2}>
          No user cards found. Sync your registered users to populate CRM leads.
        </Typography>
        <Button
          variant="contained"
          startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
          onClick={handleSync}
          disabled={syncing}
          size="large"
        >
          {syncing ? 'Syncing Users…' : 'Sync All Users to CRM'}
        </Button>
      </Paper>
    );
  }

  const current = cards[index];
  const progress = Math.round(((index + 1) / cards.length) * 100);

  return (
    <Box>
      {/* Sync button + count */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Typography variant="body2" color="text.secondary">
          {total} user card{total !== 1 ? 's' : ''}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? 'Syncing…' : 'Sync Users'}
        </Button>
      </Stack>

      {/* Progress bar */}
      <Stack direction="row" alignItems="center" gap={2} mb={1.5}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ flex: 1, height: 6, borderRadius: 3 }}
        />
        <Typography variant="caption" color="text.secondary" noWrap>
          {index + 1} / {cards.length}
        </Typography>
      </Stack>

      {/* Navigation + Card */}
      <Stack direction="row" alignItems="flex-start" gap={1}>
        <IconButton onClick={() => setIndex(Math.max(0, index - 1))} disabled={index === 0} sx={{ mt: 8 }}>
          <ChevronLeft />
        </IconButton>

        {current && (
          <UserCard
            lead={current}
            onOpenDetail={onOpenDetail}
            onStatusChange={onStatusChange}
            onLeadUpdated={handleCardUpdated}
          />
        )}

        <IconButton onClick={() => setIndex(Math.min(cards.length - 1, index + 1))} disabled={index === cards.length - 1} sx={{ mt: 8 }}>
          <ChevronRight />
        </IconButton>
      </Stack>

      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
        Use arrows to shuffle through user cards
      </Typography>
    </Box>
  );
}
